/**
 * FormFieldError — hiển thị lỗi validation inline dưới input
 * Dùng với TanStack Form field API.
 */

export function extractErrorMessage(e: unknown): string {
    if (!e) return '';
    if (typeof e === 'string') return e;
    if (typeof e === 'object' && e !== null) {
        if ('message' in e && typeof (e as any).message === 'string') {
            return (e as any).message;
        }
        if ('error' in e && typeof (e as any).error === 'string') {
            return (e as any).error;
        }
        try {
            return JSON.stringify(e);
        } catch {
            return String(e);
        }
    }
    return String(e);
}

/** Gộp danh sách lỗi (chuỗi hoặc object zod) thành một chuỗi, tránh hiển thị "[object Object]". */
export function joinFieldErrors(errors: unknown[] | undefined | null, separator = ', '): string {
    if (!errors || errors.length === 0) return '';
    return Array.from(new Set(errors.map(extractErrorMessage).filter(Boolean))).join(separator);
}

export function formatFieldError(field: any, showUntouched?: boolean): string | null {
    if (!field?.state?.meta) return null;
    const { errors, isTouched } = field.state.meta;
    if (!errors || errors.length === 0) return null;

    const isSubmitted = field.form?.state?.isSubmitted ?? false;
    if (!isTouched && !showUntouched && !isSubmitted) return null;

    const formatted = errors
        .map(extractErrorMessage)
        .filter(Boolean)
        .join(', ');

    return formatted || null;
}

interface FormFieldErrorProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field: any;
    showUntouched?: boolean;
    className?: string;
}

export function FormFieldError({ field, showUntouched, className }: FormFieldErrorProps) {
    const errorText = formatFieldError(field, showUntouched);
    if (!errorText) return null;

    return (
        <p className={className || "text-[11px] font-bold text-destructive mt-1"} role="alert">
            {errorText}
        </p>
    );
}
